import React, { useState, useEffect } from 'react'
import { Button, Form, Row, Col, Divider, Space, Alert } from 'antd'
import {
  errorNotification,
  successNotification,
  carMakesUrl,
  carModelsUrl,
  customDutyBodyTypesUrl,
  fuelTypesUrl,
  driveTrainsUrl,
  originsUrl,
  conditionsUrl,
  getRequest,
  postRequest,
  customDutiesAiCreateUrl
} from '../../config'
import BasicInput from '../../components/Forms/BasicInput'
import SelectInput from '../../components/Forms/SelectInput'
import SelectSearch from '../../components/Forms/SelectInputInteractive'
import PropTypes from 'prop-types'

// In-scope market currencies for AI MSRP estimation. The backend resolves the currency
// from the car's origin when MSRP is left blank; this selector only matters when the
// operator overrides the MSRP manually.
const currencyOptions = ['CNY', 'USD', 'JPY', 'KRW', 'EUR', 'AED'].map(c => ({
  value: c,
  label: c
}))

const AIFormPage = props => {
  const [loading, setLoading] = useState(false)
  const [catLoading, setCatLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [serverErrors, setServerErrors] = useState({})

  // Customer details are intentionally omitted in v1 of the AI flow.
  const [payload, setPayload] = useState({
    make: '',
    model: '',
    condition: '',
    fuelType: '',
    origin: '',
    bodyType: '',
    driveTrain: '',
    trim: '',
    carYear: new Date().getFullYear(),
    chassisNumber: '',
    engineSize: '',
    passengers: 5,
    isSpecialPurpose: false,
    // MSRP is OPTIONAL — leave blank to let the backend fetch it via AI.
    msrpInUsd: '',
    msrpCurrency: undefined
  })

  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  // Free-text search queries for the make/model search-selects (make is upper-cased).
  const [makeQuery, setMakeQuery] = useState('')
  const [modelQuery, setModelQuery] = useState('')
  const [bodyTypes, setBodyTypes] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [driveTrains, setDriveTrains] = useState([])
  const [origins, setOrigins] = useState([])
  const [conditions, setConditions] = useState([])

  const getYears = () => {
    const years = []
    for (let i = new Date().getFullYear() + 1; i >= 1960; i--) {
      years.push({ value: i, label: i })
    }
    return years
  }

  const fetchData = async (url, setter, label) => {
    const response = await getRequest(`${url}/all`, label, setCatLoading)
    if (response.success) {
      setter(
        response.data.map(item => ({
          value: item.id,
          label: item.name || item.country || item.country_name
        }))
      )
    }
  }

  // Makes use the brand NAME as the submitted value (so a free-text brand can be sent), while
  // retaining the id so we can still fetch a make's models from the `for-make` endpoint.
  const fetchMakes = async () => {
    const response = await getRequest(`${carMakesUrl}/all`, 'makes', setCatLoading)
    if (response.success) {
      setMakes(
        response.data.map(item => ({
          value: item.name,
          label: item.name,
          id: item.id
        }))
      )
    }
  }

  useEffect(() => {
    fetchMakes()
    fetchData(customDutyBodyTypesUrl, setBodyTypes, 'bodyTypes')
    fetchData(fuelTypesUrl, setFuelTypes, 'fuelTypes')
    fetchData(driveTrainsUrl, setDriveTrains, 'driveTrains')
    fetchData(originsUrl, setOrigins, 'origins')
    fetchData(conditionsUrl, setConditions, 'conditions')
  }, [])

  // Load model suggestions for the chosen make. payload.make holds the make NAME, so resolve
  // its id from the loaded makes list; a brand-new make simply has no existing models yet.
  useEffect(() => {
    if (!payload.make) {
      setModels([])
      return
    }
    const found = makes.find(
      m => (m.value || '').toUpperCase() === payload.make.toUpperCase()
    )
    if (!found) {
      setModels([])
      return
    }
    const getModels = async () => {
      const response = await postRequest(
        `${carModelsUrl}/for-make`,
        { make: found.id },
        'models',
        setCatLoading
      )
      if (response.success) {
        setModels(
          response.data.map(item => ({ value: item.name, label: item.name }))
        )
      }
    }
    getModels()
  }, [payload.make, makes])

  const handleInputChange = field => event => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value
    setPayload({ ...payload, [field]: value })
  }

  const handleSelect = field => value => {
    setPayload({ ...payload, [field]: value })
  }

  // Make/Model are searchable selects that also accept free text: the typed query is appended
  // as a selectable option so a brand/model not yet in the list can still be submitted (the
  // backend get-or-creates it). Make is upper-cased to match how brands are stored.
  const handleMake = value => {
    setPayload({ ...payload, make: value, model: '' })
    setMakeQuery('')
    setModelQuery('')
  }

  const handleModel = value => {
    setPayload({ ...payload, model: value })
    setModelQuery('')
  }

  const handleMakeSearch = value => setMakeQuery((value || '').toUpperCase())
  const handleModelSearch = value => setModelQuery(value || '')

  // Returns the option list plus, when needed, the typed query and the current value as
  // selectable options so free-text entries render and submit correctly.
  const withFreeText = (list, query, current) => {
    const options = list.map(o => ({ value: o.value, label: o.label }))
    const ensure = v => {
      const val = (v || '').toString().trim()
      if (
        val &&
        !options.some(o => (o.value || '').toString().toUpperCase() === val.toUpperCase())
      ) {
        options.push({ value: val, label: val })
      }
    }
    ensure(query)
    ensure(current)
    return options
  }

  const handleSubmit = async () => {
    if (loading) return // guard against duplicate submits during the sync AI fetch
    setLoading(true)
    // MSRP is optional. Strip formatting; a blank value must reach the backend as null
    // (which triggers the AI fetch), never '' or NaN (which the DecimalField rejects).
    const raw = String(payload.msrpInUsd ?? '').replace(/[^0-9.-]/g, '')
    const msrp = raw === '' ? null : Number(raw)
    const body = { ...payload, msrpInUsd: Number.isFinite(msrp) ? msrp : null }

    const res = await postRequest(
      customDutiesAiCreateUrl,
      body,
      'duty request',
      setLoading
    )

    if (res.success) {
      // The backend only creates the record once an MSRP exists (supplied or AI-fetched),
      // so a success here always has the MSRP we need.
      const ai = res.data && res.data.aiMsrp
      const conf =
        ai && ai.confidenceScore != null
          ? ` (AI confidence ${Math.round(ai.confidenceScore * 100)}%)`
          : ''
      successNotification(`Request created and priced${conf}.`)
      props.handleCancel()
    } else if (res.error && res.error.response) {
      // AI failure / validation error -> nothing was created. Keep the form open with the
      // entered values and surface the reason so the operator can retry or enter an MSRP.
      const data = res.error.response.data || {}
      if (data.message) errorNotification(data.message)
      setServerErrors(data)
    }
  }

  return (
    <Form layout="vertical">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Leave MSRP blank to fetch it automatically with AI in the car's original-market currency. VIN/chassis lookup applies to USA & Canada only."
      />

      <Divider orientation="left">Vehicle Details</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <SelectSearch
            label="Make"
            placeholder="Type or select a make"
            value={payload.make}
            onChange={handleMake}
            optionList={withFreeText(makes, makeQuery, payload.make)}
            showSearch={true}
            onSearch={handleMakeSearch}
            searchValue={makeQuery}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectSearch
            label="Model"
            placeholder="Type or select a model"
            value={payload.model}
            onChange={handleModel}
            optionList={withFreeText(models, modelQuery, payload.model)}
            showSearch={true}
            onSearch={handleModelSearch}
            searchValue={modelQuery}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Year"
            value={payload.carYear}
            onChange={handleSelect('carYear')}
            optionList={getYears()}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Trim"
            value={payload.trim}
            handleChange={handleInputChange('trim')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Chassis Number"
            value={payload.chassisNumber}
            handleChange={handleInputChange('chassisNumber')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Engine Size (L)"
            inputType="number"
            value={payload.engineSize}
            handleChange={handleInputChange('engineSize')}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Body Type"
            value={payload.bodyType}
            onChange={handleSelect('bodyType')}
            optionList={bodyTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Fuel Type"
            value={payload.fuelType}
            onChange={handleSelect('fuelType')}
            optionList={fuelTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Drive Train"
            value={payload.driveTrain}
            onChange={handleSelect('driveTrain')}
            optionList={driveTrains}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Origin"
            value={payload.origin}
            onChange={handleSelect('origin')}
            optionList={origins}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Condition"
            value={payload.condition}
            onChange={handleSelect('condition')}
            optionList={conditions}
            required
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Passengers"
            inputType="number"
            value={payload.passengers}
            handleChange={handleInputChange('passengers')}
          />
        </Col>
      </Row>

      <Divider orientation="left">MSRP (optional)</Divider>
      <Row gutter={16} align="bottom">
        <Col span={8}>
          <BasicInput
            label="MSRP — leave blank for AI"
            inputType="number"
            value={payload.msrpInUsd}
            handleChange={handleInputChange('msrpInUsd')}
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Currency (only if MSRP entered)"
            value={payload.msrpCurrency}
            onChange={handleSelect('msrpCurrency')}
            optionList={currencyOptions}
          />
        </Col>
        <Col span={8}>
          <Form.Item label="Special Purpose">
            <input
              type="checkbox"
              checked={payload.isSpecialPurpose}
              onChange={handleInputChange('isSpecialPurpose')}
            />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <Space>
          <Button onClick={props.handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Fetching MSRP & pricing…' : 'Create & Price with AI'}
          </Button>
        </Space>
      </div>
    </Form>
  )
}

AIFormPage.propTypes = {
  handleCancel: PropTypes.func
}

export default AIFormPage
